import { FormEvent, useEffect, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate, useParams } from "react-router-dom"
import {
  Organisation,
  OrganisationInput,
  createOrganisation,
  deactivateOrganisation,
  fetchOrganisation,
  fetchOrganisationen,
  updateOrganisation,
} from "@/lib/api"
import { AdressenSection } from "@/components/AdressenSection"
import { KontakteSection } from "@/components/KontakteSection"
import { RufnummernSection } from "@/components/RufnummernSection"
import { EMailsSection } from "@/components/EMailsSection"
import { ZertifikateSection } from "@/components/ZertifikateSection"
import { DokumenteSection } from "@/components/DokumenteSection"
import { LieferbedingungenZuordnungSection } from "@/components/LieferbedingungenZuordnungSection"
import { FinanzenSection } from "@/components/FinanzenSection"
import { useToast } from "@/components/Toast"
import { SkeletonZeile } from "@/components/Skeleton"
import { Button, FieldError, Input, Select } from "@/components/ui"

const TYP_OPTIONEN: [string, string][] = [
  ["kunde", "Kunde"],
  ["lieferant", "Lieferant"],
  ["behoerde", "Behörde"],
  ["dienstleister", "Dienstleister"],
]

const LEER: OrganisationInput = {
  name: "",
  name2: "",
  kuerzel: "",
  typen: [],
  internet: "",
  maildomain: "",
  mailmuster: "",
  zusatzinfo: "",
  zentraleId: null,
}

interface DetailContentProps {
  /** Bestehende Organisation (id) oder `null` für „Neue Organisation". */
  organisationId: string | null
  /** Nach erfolgreichem Speichern — Parent entscheidet Navigation/Sheet-Wechsel. */
  onGespeichert?: (org: Organisation) => void
  /** Nach Deaktivieren — z. B. Sheet schließen. */
  onGeloescht?: () => void
}

/**
 * Detail-/Bearbeiten-Inhalt einer Organisation — einbettbar in die Route
 * (OrganisationDetailPage) UND in das Rechts-Sheet der Liste (AP-2.2).
 * Enthält keine Seiten-Chrome (kein `<main>`/Zurück-Link) — das liefert der
 * jeweilige Container.
 */
export function OrganisationDetailContent({
  organisationId,
  onGespeichert,
  onGeloescht,
}: DetailContentProps) {
  const isNeu = organisationId === null
  const queryClient = useQueryClient()
  const toast = useToast()

  const [form, setForm] = useState<OrganisationInput>(LEER)
  const [fehler, setFehler] = useState<string | null>(null)
  const [nameFehler, setNameFehler] = useState<string | null>(null)

  const { data: org, isLoading } = useQuery({
    queryKey: ["organisation", organisationId],
    queryFn: () => fetchOrganisation(organisationId!),
    enabled: !isNeu && !!organisationId,
  })

  const { data: zentralenListe } = useQuery({
    queryKey: ["organisationen", "zentrale-auswahl"],
    queryFn: () => fetchOrganisationen({ size: 200 }),
  })

  useEffect(() => {
    if (org) {
      setForm({
        name: org.name,
        name2: org.name2 ?? "",
        kuerzel: org.kuerzel ?? "",
        typen: org.typen,
        internet: org.internet ?? "",
        maildomain: org.maildomain ?? "",
        mailmuster: org.mailmuster ?? "",
        zusatzinfo: org.zusatzinfo ?? "",
        zentraleId: org.zentraleId,
      })
    } else if (isNeu) {
      setForm(LEER)
    }
  }, [org, isNeu])

  const speichern = useMutation({
    mutationFn: (payload: OrganisationInput) =>
      isNeu ? createOrganisation(payload) : updateOrganisation(organisationId!, payload),
    onSuccess: (result) => {
      setFehler(null)
      queryClient.invalidateQueries({ queryKey: ["organisationen"] })
      queryClient.invalidateQueries({ queryKey: ["organisation", result.id] })
      toast.show("Organisation gespeichert.", "success")
      onGespeichert?.(result)
    },
    onError: (err: unknown) => {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setFehler(detail ?? "Speichern fehlgeschlagen.")
      toast.show(detail ?? "Speichern fehlgeschlagen.", "error")
    },
  })

  const deaktivieren = useMutation({
    mutationFn: () => deactivateOrganisation(organisationId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["organisationen"] })
      queryClient.invalidateQueries({ queryKey: ["organisation", organisationId] })
      toast.show("Organisation deaktiviert.", "success")
      onGeloescht?.()
    },
    onError: () => toast.show("Deaktivieren fehlgeschlagen.", "error"),
  })

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!form.name.trim()) {
      setNameFehler("Name ist erforderlich.")
      document.getElementById("org-name")?.focus()
      return
    }
    setNameFehler(null)
    speichern.mutate(form)
  }

  function toggleTyp(t: string) {
    setForm((f) => ({
      ...f,
      typen: f.typen?.includes(t) ? f.typen.filter((x) => x !== t) : [...(f.typen ?? []), t],
    }))
  }

  if (!isNeu && isLoading) {
    return (
      <div className="space-y-3">
        <SkeletonZeile className="w-40" />
        <SkeletonZeile className="h-8 w-72" />
        <SkeletonZeile className="h-40 w-full" />
      </div>
    )
  }

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold text-slate-900">
        {isNeu ? "Neue Organisation" : org?.name ?? "Organisation"}
      </h1>

      <form
        onSubmit={onSubmit}
        noValidate
        className="space-y-4 rounded-lg border border-slate-200 bg-white p-6 shadow-sm"
      >
        {fehler && <p className="rounded-md bg-red-50 p-2 text-sm text-red-700">{fehler}</p>}

        <div>
          <label htmlFor="org-name" className="mb-1 block text-sm font-medium text-slate-700">
            Name *
          </label>
          <Input
            id="org-name"
            required
            value={form.name}
            onChange={(e) => {
              setForm({ ...form, name: e.target.value })
              if (nameFehler) setNameFehler(null)
            }}
            invalid={!!nameFehler}
            aria-describedby={nameFehler ? "org-name-fehler" : undefined}
          />
          <FieldError id="org-name-fehler">{nameFehler}</FieldError>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Name 2</label>
            <Input
              value={form.name2 ?? ""}
              onChange={(e) => setForm({ ...form, name2: e.target.value })}
            />
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Kürzel</label>
            <Input
              value={form.kuerzel ?? ""}
              onChange={(e) => setForm({ ...form, kuerzel: e.target.value })}
            />
          </div>
        </div>

        <div>
          <span className="mb-1 block text-sm font-medium text-slate-700">Typ</span>
          <div className="flex flex-wrap gap-3">
            {TYP_OPTIONEN.map(([value, label]) => (
              <label key={value} className="flex items-center gap-1.5 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={form.typen?.includes(value) ?? false}
                  onChange={() => toggleTyp(value)}
                  className="rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Zentrale</label>
          <Select
            value={form.zentraleId ?? ""}
            onChange={(e) => setForm({ ...form, zentraleId: e.target.value || null })}
          >
            <option value="">— keine (ist selbst Zentrale) —</option>
            {zentralenListe?.data
              .filter((o) => o.id !== organisationId)
              .map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                </option>
              ))}
          </Select>
        </div>

        <div>
          <label className="mb-1 block text-sm font-medium text-slate-700">Internet-Adresse</label>
          <Input
            value={form.internet ?? ""}
            onChange={(e) => setForm({ ...form, internet: e.target.value })}
          />
        </div>

        <div className="flex justify-between pt-2">
          <Button type="submit" disabled={speichern.isPending}>
            {speichern.isPending ? "Speichert…" : "Speichern"}
          </Button>
          {!isNeu && org?.aktiv && (
            <Button type="button" variant="destructive" onClick={() => deaktivieren.mutate()}>
              Deaktivieren
            </Button>
          )}
          {!isNeu && org && !org.aktiv && (
            <span className="rounded-full bg-slate-100 px-3 py-2 text-sm text-slate-500">
              Deaktiviert
            </span>
          )}
        </div>
      </form>

      {!isNeu && organisationId && (
        <div className="space-y-6">
          <AdressenSection organisationId={organisationId} />
          <KontakteSection organisationId={organisationId} />
          <RufnummernSection organisationId={organisationId} />
          <EMailsSection organisationId={organisationId} />
          <ZertifikateSection organisationId={organisationId} />
          <DokumenteSection organisationId={organisationId} />
          <LieferbedingungenZuordnungSection organisationId={organisationId} />
          <FinanzenSection organisationId={organisationId} />
        </div>
      )}
    </div>
  )
}

/** Route-Variante `/organisationen/:id` (Deep-Link-Fallback zum Listen-Sheet). */
export function OrganisationDetailPage() {
  const { id } = useParams<{ id: string }>()
  const isNeu = id === "neu"
  const navigate = useNavigate()

  useEffect(() => {
    document.title = isNeu
      ? "Neue Organisation — RMNC Stammdaten"
      : "Organisation — RMNC Stammdaten"
  }, [isNeu])

  return (
    // Linksbündig wie Technik-Vorlage; max-w hält das Formular lesbar
    <div className="max-w-3xl">
      <Link
        to="/organisationen"
        className="mb-4 inline-block rounded-md text-sm text-slate-500 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
      >
        ← Zurück zur Liste
      </Link>
      <OrganisationDetailContent
        organisationId={isNeu ? null : id ?? null}
        onGespeichert={(org) => {
          if (isNeu) navigate(`/organisationen/${org.id}`, { replace: true })
        }}
        onGeloescht={() => navigate("/organisationen")}
      />
    </div>
  )
}
